import { getApiBaseUrl } from "@/config/runtime";
import type {
  DataResponse,
  GenerateOutlineRequest,
  GenerateSlidesRequest,
  PaginatedResponse,
  PresentationProjectCreate,
  PresentationProjectResponse,
  PresentationProjectSummary,
  PresentationProjectUpdate,
  PresentationVersionSummary,
  PreviewResponse,
  RefineSlideRequest,
} from "@/types";
import { api, getHeaders } from "../api";

const PRES_PREFIX = "/apps/presentations";

export const presentationsService = {
  // Project CRUD
  listProjects: (page = 1, pageSize = 20, status?: string) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (status) params.set("status", status);
    return api.get<PaginatedResponse<PresentationProjectSummary>>(`${PRES_PREFIX}/projects?${params}`);
  },

  getProject: (uuid: string) => api.get<DataResponse<PresentationProjectResponse>>(`${PRES_PREFIX}/projects/${uuid}`),

  createProject: (data: PresentationProjectCreate) =>
    api.post<DataResponse<PresentationProjectResponse>>(`${PRES_PREFIX}/projects`, data),

  updateProject: (uuid: string, data: PresentationProjectUpdate) =>
    api.put<DataResponse<PresentationProjectResponse>>(`${PRES_PREFIX}/projects/${uuid}`, data),

  deleteProject: (uuid: string) => api.delete<{ message: string }>(`${PRES_PREFIX}/projects/${uuid}`),

  // Outline
  generateOutline: (uuid: string, data: GenerateOutlineRequest) =>
    api.post<{ operation_id: string }>(`${PRES_PREFIX}/projects/${uuid}/outline/generate/stream`, data),

  updateOutline: (uuid: string, data: { slides: unknown[] }) =>
    api.put<DataResponse<PresentationProjectResponse>>(`${PRES_PREFIX}/projects/${uuid}/outline`, data),

  // Generation
  generateSlides: (uuid: string, data: GenerateSlidesRequest) =>
    api.post<{ operation_id: string }>(`${PRES_PREFIX}/projects/${uuid}/generate/stream`, data),

  // Refinement
  refineSlides: (uuid: string, data: RefineSlideRequest) =>
    api.post<{ operation_id: string }>(`${PRES_PREFIX}/projects/${uuid}/refine/stream`, data),

  // Preview
  getPreview: (uuid: string) => api.get<PreviewResponse>(`${PRES_PREFIX}/projects/${uuid}/preview`),

  // Versions
  listVersions: (uuid: string) =>
    api.get<DataResponse<PresentationVersionSummary[]>>(`${PRES_PREFIX}/projects/${uuid}/versions`),

  // Export (returns binary)
  exportPresentation: async (uuid: string, versionUuid?: string): Promise<Blob> => {
    const params = versionUuid ? `?version_uuid=${versionUuid}` : "";
    const baseUrl = getApiBaseUrl();
    const headers = await getHeaders();
    const response = await fetch(`${baseUrl}${PRES_PREFIX}/projects/${uuid}/export${params}`, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Export failed: ${errorText || response.statusText}`);
    }
    return response.blob();
  },
};
